/**
 * Connections applications come in multiple variants, each represented
 * by a different schema. This number will increase over time as new schema
 * versions are created. This UI aims to be as schema agnostic as possible.
 * One way in which it is able to achieve this is by "blindly" displaying
 * application data from the API "as-is" (and filtering out fields it does not
 * require using array destructuring). This enables a all versions of a schema
 * to be presented using a single component.
 *
 * This module creates human readable keys to support the above process.
 */
// type TDetail = Record<string, number>
const INSTALLER_CUST_DETAIL_KEYS: Record<string, string> = {
    mpan: "MPAN",
    installerEmail: "Installer Email",
    installationPostCode: "Post Code",
    customerEmail: "Customer Email",
    installerName: "Installer Name",
    installerPhone: "Installer Phone",
    installerCompany: "Installer Company",
    customerName: "Customer",
    customerPhone: "Customer Phone",
    mainAddress: "Customer Address",
    installerAddress: "Installer Address",
    installerPostcode: "Installer Post Code",
}

const SUPPLY_KEYS: Record<string, string> = {
    isNoOtherIssuesDNOServiceEquipment: "No Issues with Service Equipment?",
    maxImportCapacity: "Max Import Capacity (kVA)",
    declaredVoltageAtConnectionPoint: "Connection Point Voltage (V)",
    isNoSafetyConcerns: "Installer confirms there are no safety concerns?",
    importLimitingDeviceMaxDemand: "Max Demand of Import Limit Device (Amps)",
    importLimitingDeviceComments: "Import Limit Device Comments",
    isNoIssues: "Installer confirms the supply is free of any issues",
    supplyCapacity: "Supply Capacity",
    isCtMetered: "CT Metered?",
    isImportLimitingDevicePresent: "Import Limit Device?",
    premisesCutOutRating: "Cut-Out Rating (Amps)",
    isLoopedSupply: "Is looped (installer input)?",
    phaseCode: "Phases",
    energyStorageCapacity: "Battery Storage Capacity (kWh)",
}

const ENA_ASSESSMENT_KEYS: Record<string, string> = {
    validCutOutImageProvided: "Valid Cut-Out Image Provided?",
    cutOutIsModern: "Cut-Out is Modern?",
    cutOutIsOkayCondition: "Cut-Out is in Okay Condition?",
    isCutOutRatingWithinDNOThreshold: "Cut-Out Rating Within DNO Threshold?",
    isCutOutRatingKnown: "Is Cut-Out Rating Known?",
    isSupplySecure:
        "Demand less than cut-out rating & installer says no supply issues?",
    isNotOnLoopedSupply: "Is Supply Not Looped?",
    isGenerationEquipmentEnaCompliant: "Generation Equipment ENA Compliant?",
    isDemandEquipmentEnaCompliant: "Demand Equipment ENA Compliant?",
    allDevicesDemandDeviceRatingWithinDNOThreshold:
        "Demand Device Rating Within DNO threshold for all Devices?",
    isGenerationCapacityWithinDNOThreshold:
        "Generation Capacity Within DNO Threshold?",
    isWholePropertyDemandWithinDNOThreshold:
        "Property Demand Within DNO Threshold?",
    enaProcessingSuccessful: "Connect Direct Checks Run Without Error?",
    mpanThresholdsFound: "MPAN Located in DNO Provided Data?",
    devicesHaveNoAlerts: "No device alerts (at time of assessment)?",
}

const PROPOSED_DEVICE_KEYS: Record<string, string> = {
    energySource: "Energy Source",
    targetInstallDate: "Connection Target Date",
    deviceMaxCurrentDemand: "Max Current Demand (Amps)",
    numPhases: "Phases",
    powerFactor: "Power Factor",
    manufacturer: "Manufacturer",
    additionalHPComponentDetails: "Additional heat pump components?",
    isAdditionalHPComponentInstalled: "Additional heat pump component details",
    registeredCapacity: "Registered Capacity whole device (kW)",
    deviceRef: "Device Reference or Model Number",
    fuelType: "Generator Energy Source",
    generatorType: "Generator Type",
    compliesWithBSENIEC_61000_3_11: "Complies with BSENIEC 61000-3-11",
    compliesWithBSENIEC_61000_3_12: "Complies with BSENIEC 61000-3-12",
    compliesWithBSENIEC_61000_3_2: "Complies with BSENIEC 61000-3-2",
    compliesWithBSENIEC_61000_3_3: "Complies with BSENIEC 61000-3-3",
    conversionTechnology: "Conversion Technology",
}

const EXISTING_DEVICE_KEYS: Record<string, string> = {
    installDate: "Approx Install Date",
    isBeingRemoved: "Is being removed as part of this application?",
}

const COMMISSIONING_KEYS: Record<string, string> = {
    powerImbalanceLimitedBelow16A:
        "Installation limits output power imbalance to below 16 A per phase",
    isolatorSwitchLoc: "Location of isolator switch",
    cxDate: "Commissioning Date",
    interlockingInPlace:
        "Is interlocking preventing power generating modules being connected in parallel with distribution network (without synchronising) in place?",
    isolationPointsLabelled:
        "Are the isolation points labelled in accordance with EREC G99?",
    satisfiesBs7671: "Installation satisfies the requirements of BS7671",
    cyberCompliant: "Compliant with cyber security requirements?",
    suitableLockablePoints:
        "Are there suitable lockable points of isolation between the power generating module(s) and the rest of the installation?",
    notes: "Notes",
    installationLoc: "Location of installation",
    elsMeetsErecG100: "Does the Export Limiting Scheme Meet EREC G100?",
    deviceSysRef: "Device system reference (as listed on application)",
    disconnectsNoVoltDisturbance:
        "Power generating module successfully disconnects without causing a significant voltage disturbance",
    hasForcedTripOrAlarm:
        "A loss of supplies to tripping protection results in forced trip or alarm to a 24-hour staffed control centre",
    interfaceProtectionCompliant:
        "Interface protection settings comply with EREC G99?",
    interfaceProtectionLt1Sec:
        "Interface Protection operates and disconnects from the distribution network quickly (within 1 s) when a suitably rated switch, located between the PGM and the DNO connection is opened",
    reconnectGte20sec:
        "The PGM remains disconnected for at least 20s after interface protection switch is re-closed",
    isSldSameAsOriginalApplication:
        "Is SLD is the same as the original application?",
    runsInParallelNoVoltDisturbance:
        "The power generating module successfully runs in parallel with the distribution network without tripping and without causing significant voltage disturbances",
    syncsNoVoltDisturbance:
        "The power generating module successfully synchronises with the distribution network without causing significant voltage disturbance",
}

const GLOBAL_KEY_MATCHER = {
    ...INSTALLER_CUST_DETAIL_KEYS,
    ...SUPPLY_KEYS,
    ...ENA_ASSESSMENT_KEYS,
    ...PROPOSED_DEVICE_KEYS,
    ...EXISTING_DEVICE_KEYS,
    ...COMMISSIONING_KEYS,
}

/**
 * Formats and sorts object, adding in recognised human-readable keys
 * @param { TDetail } unformatted - unformatted object of key values pairs
 * @returns  { TDetail } - object with injected keys and sorted
 */
const formatKeys = <T extends object>(unformatted: T): T => {
    const formatted = (
        Object.keys(unformatted) as Array<keyof typeof unformatted>
    ).reduce(
        (acc, key) => ({
            ...acc,
            [GLOBAL_KEY_MATCHER[key as string] || key]: unformatted[key],
        }),
        {} as T
    )
    const sorted = (Object.keys(formatted) as Array<keyof typeof formatted>)
        .sort()
        .reduce(
            (acc, key) => ({
                ...acc,
                [key]: formatted[key],
            }),
            {} as T
        )
    return sorted
}

export { formatKeys }
